import { AmDestinations, AmRooms, Location } from '../common';
import { EnvConfig } from '../config';
import { HttpParams, httpRequest } from './httpUtils';
import { Lock } from './types';
import { AmAvailability } from '../store';
import { generateGroupString } from '../utils/deeplinkUtils';

export type AvailabilityParams = {
  dep: string; // origin location code
  arr: string; // destination code
  g: string;
};

const amApiCall = <T>({ amApiUrl }: EnvConfig, endpoint: string, lock: Lock): Promise<T> =>
  httpRequest<T>({
    method: 'GET',
    url: `${amApiUrl}/${endpoint}`,
    timeout: 6000,
    retries: 2,
    lock,
    withCredentials: false,
  })
    .then(({ payload }) => payload)
    .finally(() => lock.release());

const amApiCallWithParams = <T>(
  { amApiUrl }: EnvConfig,
  endpoint: string,
  params: HttpParams,
  lock: Lock
): Promise<T> => {
  return httpRequest<T>({
    method: 'GET',
    url: `${amApiUrl}/${endpoint}`,
    params,
    timeout: 6000,
    retries: 2,
    lock,
    withCredentials: false,
  })
    .then(({ payload }) => payload)
    .finally(() => lock.release());
};

const amDestinationsLock: Lock = new Lock();

export const getAMDestinations = (envConfig: EnvConfig, origin: string): Promise<AmDestinations> =>
  amApiCall<AmDestinations>(envConfig, `destinations${origin ? `/${origin}` : ''}`, amDestinationsLock)
    .then<AmDestinations>((payload) => payload)
    .catch<AmDestinations>(() => ({ holidayTypes: {}, destinations: [] }));

const amOriginsLock: Lock = new Lock();

type AmOriginPayload = {
  name: string;
  code: string;
  city: string;
  countryCode: string;
};

export const getAMOrigins = (envConfig: EnvConfig): Promise<Location[]> =>
  amApiCall<AmOriginPayload[]>(envConfig, 'origins', amOriginsLock)
    .then<Location[]>((payload) =>
      payload.map((location) => ({
        title: location.name,
        countryCode: location.countryCode,
        locationCode: location.code,
        cityName: location.city,
        country: '',
        AJBPartner: false,
        SJBPartner: false,
        airAward: false,
        airPartner: false,
        airAlliance: false,
        type: 'city',
        latitude: 0,
        longitude: 0,
      }))
    )
    .catch<Location[]>(() => []);

const amAvailabilityLock: Lock = new Lock();

export const getAMAvailability = (
  envConfig: EnvConfig,
  originLocationCode: string,
  destinationLocationCode: string,
  group: AmRooms
): Promise<AmAvailability> => {
  return amApiCallWithParams<AmAvailability>(
    envConfig,
    'availability',
    new HttpParams({
      dep: originLocationCode,
      arr: destinationLocationCode,
      g: generateGroupString(group),
    }),
    amAvailabilityLock
  )
    .then<AmAvailability>((payload) => payload)
    .catch<AmAvailability>(() => ({}));
};
